import { useMemo } from 'react';
import { RouterLink } from '@redux-first-router-link';

import DesktopSidebar from '@/components/DesktopSidebar';
import MobileTopbarSubNav, { ListItem } from '@/components/MobileTopbarSubNav';
import { TwoColumnWithDesktopSidebar } from '@/components/Layout';

import useUser from '@/hooks/useUser';
import { useTranslation } from '@/hooks/useTranslation';

import PlansPage from '@/pages/Plans';

import {
  ROUTE_USER_PROFILE,
  ROUTE_USER_NOTIFICATIONS,
  ROUTE_USER_BETA_LAB,
  ROUTE_PLANS,
  ROUTE_USER_SUBSCRIPTION,
  ROUTE_USER_SUBSCRIPTION_AND_BILLING,
  ROUTE_PRO_API,
  ROUTE_USER_PROFILE_LOCALE,
  ROUTE_USER_SUBSCRIPTION_LOCALE,
  ROUTE_USER_SUBSCRIPTION_AND_BILLING_LOCALE,
  ROUTE_USER_NOTIFICATIONS_LOCALE,
} from '@/constants/routes';

import loadable from '@loadable/component';
import { useProApiAccess } from '@/pages/ProApi/hooks/useProApiAccess';
import { useUserAccess } from '@/hooks/useUserAccess';
import { useLocale } from '@/hooks/useLocale';

const Profile = loadable(() => import('./components/Profile'));
const Subscription = loadable(() => import('./components/Subscription'));
const SubscriptionAndBilling = loadable(
  () => import('./components/SubscriptionAndBilling'),
  { resolveComponent: (component) => component.SubscriptionAndBilling }
);
const Notifications = loadable(
  () => import('./components/Notifications/Notifications')
);
const BetaLab = loadable(() => import('./components/BetaLab'));
const ProApi = loadable(() => import('@/pages/ProApi'), {
  resolveComponent: (c) => c.ProApi,
});
export interface UserProps {
  route: string;
}

const User = ({ route }: UserProps) => {
  const {
    data: { billing },
  } = useUser();
  const { data: userAccess } = useUserAccess();
  const isAvanzaUser = userAccess.brokerReferrer === 'avanza';
  const t = useTranslation();
  const locale = useLocale();

  const { canCreateApiKeys } = useProApiAccess();

  const accountRoutes = useMemo(() => {
    const routes = [
      {
        to: locale !== 'en' ? ROUTE_USER_PROFILE_LOCALE : ROUTE_USER_PROFILE,
        label: t('app.user.profile.profile'),
        payload: { locale },
      },
      ...(RUNTIME_ENV !== 'native'
        ? ([
            {
              to:
                locale !== 'en'
                  ? ROUTE_USER_SUBSCRIPTION_AND_BILLING_LOCALE
                  : ROUTE_USER_SUBSCRIPTION_AND_BILLING,
              label: t('app.user.profile.subscriptionAndBilling'),
              payload: { locale },
            },
            { to: ROUTE_PLANS, label: t('app.user.profile.plansAndPricing') },
            {
              to:
                locale !== 'en'
                  ? ROUTE_USER_SUBSCRIPTION_LOCALE
                  : ROUTE_USER_SUBSCRIPTION,
              label: t('app.user.profile.manageSubscription'),
              payload: { locale },
            },
          ] as const)
        : ([
            { to: ROUTE_PLANS, label: t('app.user.profile.myPlan') },
          ] as const)),
      {
        to: ' ',
        label: t('app.user.profile.viewedCompanies'),
      },
      {
        to:
          locale !== 'en'
            ? ROUTE_USER_NOTIFICATIONS_LOCALE
            : ROUTE_USER_NOTIFICATIONS,
        label: t('app.user.profile.notifications'),
      },
    ] as const;

    const mutableRoutes: Array<{
      to:
        | (typeof routes)[number]['to']
        | typeof ROUTE_USER_BETA_LAB
        | typeof ROUTE_PRO_API;
      label: string;
    }> = [...routes];

    if (RUNTIME_ENV !== 'native') {
      mutableRoutes.push({
        to: ROUTE_USER_BETA_LAB,
        label: t('app.user.profile.betaLab'),
      });
    }

    return mutableRoutes;
  }, [locale, t]);

  const mobileRoutes = useMemo(() => {
    const mutableAccountRoutes = Array.from(accountRoutes);

    if (canCreateApiKeys) {
      mutableAccountRoutes.push({
        to: ROUTE_PRO_API,
        label: t('app.user.profile.proApi'),
      });
    }

    // Avanza users shouldn't be able to change their plan or manage subscriptions, as this is controlled by Avanza.
    if (isAvanzaUser) {
      return mutableAccountRoutes.filter(
        (n) =>
          ![
            t('app.user.profile.subscriptionAndBilling'),
            t('app.user.profile.plansAndPricing'),
            t('app.user.profile.manageSubscription'),
            t('app.user.profile.viewedCompanies'),
          ].includes(n.label as string)
      );
    }

    if (billing.provider === 'stripe') {
      // Stripe - subscriptions
      return mutableAccountRoutes.filter((n) => {
        if (
          n.label === t('app.user.profile.subscriptionAndBilling') &&
          billing.status === 'trialing'
        ) {
          return false;
        }
        return ![
          t('app.user.profile.manageSubscription'),
          t('app.user.profile.viewedCompanies'),
        ].includes(n.label as string);
      });
    } else {
      // Apple/Other - subscriptions
      return mutableAccountRoutes.filter(
        (n) =>
          ![
            t('app.user.profile.subscriptionAndBilling'),
            t('app.user.profile.plansAndPricing'),
            t('app.user.profile.viewedCompanies'),
          ].includes(n.label as string)
      );
    }
  }, [billing.provider, billing.status, canCreateApiKeys, t]);

  const desktopRoutes = useMemo(() => {
    const mutableAccountRoutes = Array.from(accountRoutes);

    if (canCreateApiKeys) {
      mutableAccountRoutes.push({
        to: ROUTE_PRO_API,
        label: t('app.user.profile.proApi'),
      });
    }

    // Avanza users shouldn't be able to change their plan or manage subscriptions, as this is controlled by Avanza.
    if (isAvanzaUser) {
      return mutableAccountRoutes.filter(
        (n) =>
          ![
            t('app.user.profile.subscriptionAndBilling'),
            t('app.user.profile.plansAndPricing'),
            t('app.user.profile.manageSubscription'),
            t('app.user.profile.viewedCompanies'),
          ].includes(n.label as string)
      );
    }

    if (billing.provider === 'stripe') {
      // Stripe - subscriptions
      return mutableAccountRoutes.filter(
        (n) =>
          ![t('app.user.profile.manageSubscription')].includes(
            n.label as string
          )
      );
    } else {
      // Apple/Other - subscriptions
      return mutableAccountRoutes.filter(
        (n) =>
          ![
            t('app.user.profile.subscriptionAndBilling'),
            t('app.user.profile.plansAndPricing'),
          ].includes(n.label as string)
      );
    }
  }, [accountRoutes, canCreateApiKeys, isAvanzaUser, billing.provider, t]);

  return (
    <TwoColumnWithDesktopSidebar>
      <MobileTopbarSubNav routes={mobileRoutes} selected={route}>
        {({ routes, handleClick, refMap, selected }) =>
          routes.map(({ to, label, tracking }, i) => (
            <ListItem
              ref={refMap[i]}
              key={`MobileTopbarListItem-${to}-${i}`}
              selected={selected === to}
            >
              <RouterLink
                inheritColor
                noUnderline
                to={{ type: to || '' }}
                onClick={() => handleClick(tracking || label, to)}
              >
                {label}
              </RouterLink>
            </ListItem>
          ))
        }
      </MobileTopbarSubNav>
      <DesktopSidebar routes={desktopRoutes} selected={route} />
      {(route === ROUTE_USER_PROFILE ||
        route === ROUTE_USER_PROFILE_LOCALE) && <Profile />}
      {(route === ROUTE_USER_NOTIFICATIONS ||
        route === ROUTE_USER_NOTIFICATIONS_LOCALE) && <Notifications />}
      {route === ROUTE_PLANS && RUNTIME_ENV === 'native' && <PlansPage />}
      {(route === ROUTE_USER_SUBSCRIPTION ||
        route === ROUTE_USER_SUBSCRIPTION_LOCALE) && <Subscription />}
      {(route === ROUTE_USER_SUBSCRIPTION_AND_BILLING ||
        route === ROUTE_USER_SUBSCRIPTION_AND_BILLING_LOCALE) && (
        <SubscriptionAndBilling />
      )}
      {route === ROUTE_USER_BETA_LAB && <BetaLab />}
      {route === ROUTE_PRO_API && <ProApi />}
    </TwoColumnWithDesktopSidebar>
  );
};

export default User;
